import { useQuery, QueryHookOptions, useSubscription } from '@apollo/client';
import React, { useMemo, useState, useEffect } from 'react';

import Navigation from './Navigation';
import NAVIGATION_LIST from './NavigationList.gql';
import UNREADS from './Unreads.gql';
import type {
  NavigationList,
  NavigationListVariables,
  NavigationList_navigationList_items_NavigationListEntityItem as NavigationListEntityItem,
} from './__generated__/NavigationList';
import type { UnreadsQuery } from './__generated__/UnreadsQuery';
import NavListUnreadsSubscription from './NavListUnreads.hq.graphql';
import type { NavListUnreadsHQ } from './__generated__/NavListUnreadsHQ';

import getUINavItems from './navItems';
import getMyUnreads from './myUnreads';
import './styles/Navigation.scss';
import { useIntl } from 'lib/intl';

export const TOUCH_KEY = 'NAVIGATION_LIST';

export type Props = {};

export default function NavigationWrapper(props: Props) {
  const { loading, error, data } = useNavigationList();

  const { loading: loadingUnreads, data: dataUnreads } = useQuery<UnreadsQuery>(UNREADS);
  const intl = useIntl();

  const navigationList = data?.navigationList;
  const me = dataUnreads?.me ?? undefined;

  const [selectedItemUuid, setSelectedItemUuid] = useState<string | null>();

  const myUnreads = useMemo(() => getMyUnreads(me), [me]);
  const items = useMemo(
    () => getUINavItems(navigationList, myUnreads),
    [navigationList, myUnreads]
  );

  return (
    <Navigation
      loading={loading || loadingUnreads}
      error={Boolean(error)}
      items={items}
      groupsUnreadsTotal={myUnreads['groupsUnreadsTotal'] ?? 0}
      activeItem={selectedItemUuid}
      onClick={(uuid) => setSelectedItemUuid(uuid)}
    />
  );
}

export function useNavigationList(
  options: QueryHookOptions<NavigationList, NavigationListVariables> = {}
) {
  const result = useQuery<NavigationList, NavigationListVariables>(NAVIGATION_LIST, {
    fetchPolicy: 'network-only',
    ...options,
  });

  // alias only for the effect
  const subscribeToMore = result.subscribeToMore;

  // hook up the gql subscription that will bring the real time updates for unreads
  useEffect(() => {
    subscribeToMore<NavListUnreadsHQ, {}>({
      document: NavListUnreadsSubscription,
      context: { hq: true },
      updateQuery: (prev, { subscriptionData }) => {
        const payload = subscriptionData.data.navigationListUnreads;
        if (!payload) {
          return prev;
        }

        const items = prev.navigationList.items.map((item) => {
          if (
            item.__typename === 'NavigationListEntityItem' &&
            item.uuid === payload.organizationUuid
          ) {
            return { ...item, unreadsCount: payload.unreadsCount };
          }

          return { ...item };
        });

        const res = {
          ...prev,
          navigationList: { ...prev.navigationList, items },
        };

        return res;
      },
    });
  }, [subscribeToMore]);

  return result;
}

/**
 *  Utility wrapper to the navlistunreads sub, it runs a callbakc when we get new unreads, that's it
 */
export function useOnUnread(callback: (payload?: NavListUnreadsHQ) => void) {
  const sub = useSubscription<NavListUnreadsHQ>(NavListUnreadsSubscription, {
    shouldResubscribe: true,
    context: { hq: true },
    onSubscriptionData: ({ subscriptionData }) => {
      callback(subscriptionData.data);
    },
  });

  const err = sub.error;
  if (err) {
    console.log('Subscribing to nav list unreads real time updates failed', err);
  }
}

/**
 * access a cached navigation list item by its uuid,
 * if it wasn't fetched before the cache will be empty and
 * nothing should be returned. This should rarely happen
 * since the navigation list is fetched on most pages of the app,
 * and on most pages of the app where you would want to access a nav item.
 */
export function useNavigationItem(uuid?: string): NavigationListEntityItem | undefined {
  const { data } = useNavigationList({
    // this whole hook is just a cache accessing thing
    fetchPolicy: 'cache-only',
    skip: !uuid,
  });

  // using a for of loop because .filter and .find are not type aware enough to understand this
  for (const item of data?.navigationList.items ?? []) {
    if (item.__typename === 'NavigationListEntityItem' && item.uuid === uuid) {
      return item;
    }
  }
}
